import React from "react";
import classNames from "classnames";
import styles from "./product-page-desktop.module.scss";
import { ProductPageStore, useProductPageCommon } from "./ProductPageCommon";
import FixBreadcrumb from "@/elements-react/fix-breadcrumb";
import ProductMpn from "@/elements-react/product-mpn";
import { useServices } from "@/services-context";
import { useTranslation } from "react-i18next";
import SpecificationsList from "@/elements-react/specifications-list";
import { BenefitsList } from "@/elements-react/benefits-list";
import FixImage from "@/elements-react/fix-image";
import { features } from "@/constants/features.ts";
import { LeadFormCondition, ProductStateType } from "@/util/constants.ts";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { createProductUrl } from "@/util/category.ts";
import { FixAlert } from "@/elements-react/fix-alert";
import FixText from "@/elements-react/fix-text";
import GoToReplacementButton from "@/elements-react/go-to-replacement-button";
import { useEffect } from "preact/hooks";
import { PageTags } from "@/elements-react/document-elements/page-tags";
import LeadForm from "@/pages-react/product-page/lead-form";
import { MessengersBlock } from "@/pages-react/product-page/messangers-block/MessengersBlock.tsx";

const ProductPageDesktop = (props: { store: ProductPageStore }) => {
  const { t } = useTranslation();
  const services = useServices();
  const {
    redirectMpn,
    noImageProductUrl,
    title,
    mpn,
    showRating,
    showFits,
    product,
    stockStatus,
    specifications,
    jsonLdDataset,
    metaDataset,
    seoMarkupDataset,
    hasBlock,
    hasProperty,
    renderDispatch,
    renderAdd,
    renderQuantity,
    getHasAvailableInStock,
    renderProductDiscontinuedSegment,
    renderCanBe,
    renderDiscount,
    renderPrice,
    renderTip,
    renderShipping,
    renderShippingRates,
    renderAdditionalBlock,
    renderStatus,
    renderBadge,
    renderVat,
    renderAbout,
    renderSituable,
    renderCarousel,
    configureSelections,
    renderTitle,
    getDataLeadForm,
  } = useProductPageCommon(LayoutTypes.TYPE_DESKTOP, props.store);

  useEffect(() => configureSelections(product, null), []);

  const carousel = hasBlock("carousel");
  const dispatch = hasBlock("product-dispatch");

  const aboutContent = (
    <>
      {hasBlock("about") && (
        <div className={styles["content-segment-lg"]}>
          <h2 className={styles["title"]}>{t("afp6t38xk75sr191.about", "About")}</h2>
          {renderAbout(LayoutStyleTypes.TYPE_LG)}
        </div>
      )}
      {hasBlock("situable-for") && (
        <div className={styles["content-segment-lg"]}>
          <h2 className={styles["title"]}>{t("4d431o9n9ol327o8.suitable-summary", "Suitable for")}</h2>
          <div>{renderSituable(LayoutStyleTypes.TYPE_LG)}</div>
        </div>
      )}
    </>
  );

  const leftImages = carousel ? (
    renderCarousel(LayoutStyleTypes.TYPE_LG)
  ) : (
    <div className={classNames(styles.left, styles["no-image"])}>
      <div className={styles["no-image-block"]}>
        <div className={styles["product-image"]}>
          <FixImage className="card" type={LayoutStyleTypes.TYPE_LG} src={noImageProductUrl} aspect={1.3} />
        </div>
        <div className={styles["mpn-block"]}>{mpn}</div>
      </div>
    </div>
  );

  const renderDiscontinuedStateDiscontinued = () => {
    const canBe = hasBlock("can-be");
    const descriptionText = t(
      "na780sifugbasnis.discontinued-text",
      "The official replacement from [{{product}}] is [{{exchange}}], designed to meet the same needs",
      { product: product!.mpnFormatted, exchange: product!.exchangesFinal[0].mpnFormatted },
    );
    const productUrl = createProductUrl(product!.exchangesFinal[0], services.categories, "long", product!.mpnFormatted);

    return (
      <>
        <div className={styles["content-segment-lg"]}>
          <h2 className={styles["title"]}>{t("ansd80oishdnsaid.product-is-no-produced", "This product is no longer produced")}</h2>
        </div>
        <div className={styles["container"]}>
          <FixText type={LayoutStyleTypes.TYPE_LG} text={descriptionText} />
        </div>
        <div className={styles["price-cart-group"]}>
          <div className={styles["row-top"]}>
            <GoToReplacementButton productUrl={productUrl} />
          </div>
        </div>
        {canBe && features.product_cross && (
          <>
            <div className={styles["delimiter"]}></div>
            {renderCanBe(LayoutStyleTypes.TYPE_LG)}
          </>
        )}
      </>
    );
  };

  const renderLeadForm = () => {
    const isOutOfStock = hasProperty("outOfStock");
    const hasPrice = hasProperty("hasPrice");

    const dataLeadForm = getDataLeadForm();
    const canBe = hasBlock("can-be");

    if (!dataLeadForm) {
      return;
    }

    return (
      <div className={styles["price-cart-group"]}>
        <div className={styles["status-row"]}>{isOutOfStock && renderStatus(LayoutStyleTypes.TYPE_LG)}</div>
        {hasPrice && (
          <>
            <div>
              <div className={styles["left"]}>{renderPrice(true, styles.price)}</div>
              <div className={styles["last-price-text"]}>{t("ab7r8gowieugrfbr.last-known-price", "Last known price — may change upon restock")}</div>
            </div>
          </>
        )}
        {renderTitle() && <div className={classNames(styles.title)}>{renderTitle()}</div>}
        <LeadForm
          title={dataLeadForm.title}
          subTitle={dataLeadForm.subTitle}
          buttonText={dataLeadForm.buttonText}
          formName={dataLeadForm.formName}
          mpn={mpn}
          customize={{ buttonIcon: dataLeadForm.buttonIcon }}
          formAttributes={{
            isHiddePriceIfProductIsOutOfStock: hasProperty("isHidePriceIfProductIsOutOfStock") ? "yes" : "no",
            deliveryStandardTimesIsNotDefined: hasProperty("deliveryStandardTimesIsNotDefined") ? "yes" : "no",
            hasPrice: hasProperty("hasPrice") ? "yes" : "no",
          }}
        />
        <MessengersBlock productMpn={mpn} />
        {canBe && features.product_cross && (
          <div>
            <div className={styles["delimiter"]}></div>
            <div className={styles["on-request-cross"]}>
              <h2 className={styles["can-be-title"]}>{t("c3d98ff5.analogues", "Analogues")}</h2>
              {renderCanBe(LayoutStyleTypes.TYPE_LG)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProductPricesSegment = () => {
    const discount = hasBlock("discount");
    const stock = hasBlock("stock");
    const quantityInput = hasBlock("quantity-input");
    const shipping = hasBlock("shipping-notify");

    const quantityTip = hasBlock("quantity-tip");
    const canBe = hasBlock("can-be");

    const showCrosses =
      [ProductStateType.Discontinued, ProductStateType.Inactive].includes(product?.state) ||
      (product?.state === ProductStateType.Active && !product?.prices.length);

    return (
      <>
        <div className={styles["price-cart-group"]}>
          {dispatch && <div>{renderDispatch()}</div>}
          {discount && features.discount ? (
            <div>
              {renderDiscount(true)}
              {renderBadge()}
            </div>
          ) : (
            ""
          )}
          <div className={styles["status-row"]}>{stock && renderStatus(LayoutStyleTypes.TYPE_LG)}</div>
          {
            <>
              <div className={styles["row-middle"]}>
                <div className={styles["left"]}>
                  {renderPrice(true, styles.price)} {quantityTip && renderTip(styles["quantity-tip"])}
                </div>
                <div className={styles["right"]}>
                  {renderVat()} {shipping && renderShipping()}
                </div>
              </div>
              <div className={styles["row-bottom"]}>
                {renderAdd()} {quantityInput && renderQuantity()}
              </div>
            </>
          }
        </div>
        {renderAdditionalBlock()}
        {canBe && features.product_cross ? (showCrosses ? renderCrossesForDiscontinued() : renderCrosses()) : ""}
      </>
    );
  };

  const renderCrosses = () => {
    return (
      <>
        <div className={styles["delimiter"]}></div>
        <div className={styles["can-be-wrapper"]}>
          <h2 className={styles["can-be-title"]}>{t("c3d98ff5.analogues", "Analogues")}</h2>
          {renderCanBe(LayoutStyleTypes.TYPE_LG)}
        </div>
      </>
    );
  };

  const renderCrossesForDiscontinued = () => {
    return (
      <>
        <div className={styles["arrow-up"]}></div>
        <div className={styles["discontinued-cross"]}>
          <h2 className={styles["discontinued-cross-title"]}>{t("abr78vba78k3ufhv.great-analogues", "Great analogues for this product:")}</h2>
          {renderCanBe(LayoutStyleTypes.TYPE_LG)}
        </div>
      </>
    );
  };

  // Old condition logic
  // const rightBlock =
  //   product && product.exchangesFinal.length > 0 && !getHasAvailableInStock()
  //     ? renderProductDiscontinuedSegment(renderDiscontinuedStateDiscontinued, renderDiscontinuedStateOnRequest)
  //     : renderProductPricesSegment();

  const isLeadFormState = hasProperty("onRequest") || hasProperty("noPrice") || hasProperty("outOfStock");
  const shouldRenderShippingRates =
    services.customerChannel.showShippingRates &&
    hasBlock("shipping-notify") &&
    !isLeadFormState &&
    product.state !== ProductStateType.Discontinued;

  const rightBlock = (() => {
    if (isLeadFormState) {
      return renderLeadForm();
    } else if (product.state === ProductStateType.Discontinued) {
      return renderDiscontinuedStateDiscontinued();
    } else {
      return renderProductPricesSegment();
    }
  })();

  return (
    <div className={styles.root}>
      <div className={styles["top-group"]}>
        <div className={styles["top-group-inner"]}>
          <div className={classNames(styles.row, styles.navigation)}>
            <FixBreadcrumb />
            <FixAlert />
          </div>
          {redirectMpn && (
            <div className={classNames(styles.row, styles.notification)}>
              <div className={styles["success-notify"]}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      "43ook0potrz3ufhv.looks-like",
                      "Looks like product <b>{{mpn}}</b> is no longer available. We’ve found a replacement from the manufacturer and redirected you here",
                      { mpn: redirectMpn },
                    ),
                  }}
                ></span>
              </div>
            </div>
          )}
          <div className={classNames(styles.row, styles.title)}>
            <h1 className={classNames(styles.page, styles.title)}>{title}</h1>
            <ProductMpn code={mpn} />
          </div>
          {showFits && (
            <div className={styles.fits}>
              <span className={styles.icon}>✓</span>
              <div className={styles.message}>This product fits your</div>
              <div className={styles.model}>Ford Focus 2015 EcoBoost 2.0 Titanium</div>
              <button className={styles.change} type="button">
                {t("5ie0iw1fcx65ftb6.change", "Change")}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles["left-group"]}>
          <div className={styles["product-group"]}>
            <div className={classNames(styles.leftImages)}>{leftImages}</div>
            <div className={styles.right}>
              <div className={styles["right-wrapper"]}>
                <SpecificationsList type={LayoutStyleTypes.TYPE_LG} options={specifications} />
              </div>
            </div>
          </div>
          {(hasBlock("about") || hasBlock("situable-for")) && (
            <div className={styles["about-group"]}>
              <div className={styles["details-group"]}>{aboutContent}</div>
            </div>
          )}
        </div>
        <div className={styles["right-group"]}>
          <div className={styles["controls-group"]}>{rightBlock}</div>
          {shouldRenderShippingRates && <div className={styles["shipping-rates-group"]}>{renderShippingRates(LayoutStyleTypes.TYPE_LG)}</div>}
          {features.product_related && (
            <div className={classNames(styles["controls-group"])}>
              <div className={classNames(styles["related-group"], styles["content-segment-lg"])}>
                <h2 className={styles.title}>{t("j10il6245v20kshl.related-summary", "Related products")}</h2>
                {/*<RelatedProducts type={LayoutStyleTypes.TYPE_LG} />*/}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles["benefits-group"]}>
        <BenefitsList type={LayoutStyleTypes.TYPE_LG} />
      </div>
      <PageTags jsonld={jsonLdDataset} meta={metaDataset} seo={seoMarkupDataset} />
    </div>
  );
};

export default ProductPageDesktop;
